import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { API } from '../../apis/api';
import { buildErrorMessage } from '../../apis/calls';
import { TabbedViewWithDateSelect } from '../../components';
import { AGGREGATE_STATUS, API_RESOURCE_URLS, DATE_FORMAT } from '../../constants';
import { useAjaxCallbackWithIntervals } from '../../hooks';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { getDatesForRange } from '../../utils/utils';
import EngineDressingSequence from '../EngineDressingSequence/EngineDressingSequence';

const EngineDressingSequenceView = () => {
  const { showInternalError, showError } = usePopupManager();

  const [plant] = usePlantAndLinesStore().plantState;
  const [aggregateLine] = usePlantAndLinesStore().aggregateLineState;

  const [sequenceCount, setSequenceCount] = useState(0);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [lastRefreshedDate, setLastRefreshedDate] = useState(null);
  const [hasActiveEdits, setHasActiveEdits] = useState(false);

  const [lastAgsnGenerationDate, setLastAgsnGenerationDate] = useState(null);
  const [agsnDates, setAgsnDates] = useState([]);

  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });

  const loadUsableLastAgsnGenerationDate = async (plantId, lineId) => {
    const today = moment();
    try {
      const response = await API.get(
        API_RESOURCE_URLS.lastAgSNGeneratedDate(plantId, lineId)
      );
      const { data } = response;
      const lastAgsnGenerationDateFromResponse = data.date;

      if (
        lastAgsnGenerationDateFromResponse &&
        moment(lastAgsnGenerationDateFromResponse).isSameOrAfter(today, 'day')
      ) {
        setLastAgsnGenerationDate(moment(lastAgsnGenerationDateFromResponse));
      } else {
        setLastAgsnGenerationDate(null);
      }
    } catch (error) {
      console.error(buildErrorMessage(error));
      showError({
        contextText: `Failed to Fetch Last AGSN Date`,
        error: error,
      });
    }
  };

  const loadEngineDressingSequence = async (plantId, lineId, date) => {
    if (!plantId || !lineId || !date) return;

    try {
      setRowsData(prev => ({ ...prev, loading: true }));

      const response = await API.get(
        API_RESOURCE_URLS.engineDressingSequence(plantId, lineId),
        {
          params: {
            date: date.format(DATE_FORMAT.ISO),
          },
        }
      );

      const data = (response.data || []).map((row, index) => ({
        id: row.id || index,
        ...row,
      }));

      setRowsData({
        loading: false,
        data,
        filteredData: data,
      });
      setLastRefreshedDate(moment());
    } catch (error) {
      console.error(buildErrorMessage(error));

      setRowsData({
        loading: false,
        data: [],
        filteredData: [],
      });

      showInternalError();
    }
  };

  const refreshedDate = useAjaxCallbackWithIntervals(
    rowsData.loading,
    hasActiveEdits,
    loadEngineDressingSequence,
    plant?.value,
    aggregateLine?.value,
    selectedDate
  );

  useEffect(() => {
    if (plant?.value && aggregateLine?.value) {
      loadUsableLastAgsnGenerationDate(plant.value, aggregateLine.value);
    }
  }, [plant, aggregateLine]);

  useEffect(() => {
    if (lastAgsnGenerationDate) {
      setSelectedDate(moment());
      setAgsnDates(getDatesForRange(moment(), lastAgsnGenerationDate));
    } else {
      setAgsnDates([moment()]);
    }
  }, [lastAgsnGenerationDate]);

  return (
    <TabbedViewWithDateSelect
      tabs={[
        {
          label: 'Engine Dressing Sequence',
          title: 'Engine Dressing Sequence',
          count: sequenceCount,
          component: EngineDressingSequence,
          props: {
            setSequenceCount,
            rowsData,
            lastRefreshedDate: refreshedDate || lastRefreshedDate,
            selectedDate,
            setSelectedDate,
            loadEngineDressingSequence,
            hasActiveEdits,
            setHasActiveEdits,
            agsnDates,
            lastAgsnGenerationDate,
          },
        },
      ]}
    />
  );
};

export default EngineDressingSequenceView;